import { useCallback, useEffect, useState } from "react";
import { getMyCompany } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Small shared cache so TopNav and the client pages do not refetch GET /api/companies/me on every render.
let cache = { userId: null, data: null, promise: null };

const fetchFor = (userId, force) => {
  if (!force && cache.userId === userId && (cache.data || cache.promise)) {
    return cache.data ? Promise.resolve(cache.data) : cache.promise;
  }
  const p = getMyCompany()
    .then((d) => {
      if (cache.promise === p) cache = { userId, data: d, promise: null };
      return d;
    })
    .catch((e) => {
      if (cache.promise === p) cache = { userId: null, data: null, promise: null };
      throw e;
    });
  cache = { userId, data: null, promise: p };
  return p;
};

/** Own company of a logged `cliente` (GET /api/companies/me). Disabled for other roles. */
export function useMyCompany({ enabled = true } = {}) {
  const { user } = useAuth();
  const uid = user?.id || null;
  const active = enabled && !!uid && user?.role === "cliente";
  const [data, setData] = useState(active && cache.userId === uid ? cache.data : null);
  const [loading, setLoading] = useState(active && !(cache.userId === uid && cache.data));
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!active) return null;
    setLoading(true);
    try {
      const d = await fetchFor(uid, force);
      setData(d);
      setError(null);
      return d;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [active, uid]);

  useEffect(() => {
    load(false);
  }, [load]);

  return { data, loading, error, refresh: () => load(true) };
}

export const canSeeKeys = (d) => !!(d && d.company && d.is_master && d.company.llm_ui_visible);
